import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { lastValueFrom } from 'rxjs';

import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Workflows } from 'src/providers/workflows/workflows';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { Util } from 'src/shared/util/util';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { WorkflowsPage } from 'src/pages/workflows/workflows';
import { OnboardingComponent } from 'src/shared/components/onboarding/onboarding';

@Component({
    selector: 'automate-workflows-onboarding',
    templateUrl: './automate-workflows-onboarding.html',
    styleUrls: ['./automate-workflows-onboarding.scss'],
    standalone: false
})
export class AutomateWorkflowsOnboarding implements OnInit {

  addedWorkflows: any[] = [];
  unselectedAlreadyAddedTemplates: any[] = [];
  workflowTemplates: any[] = [];
  selectedWorkflows: any[] = [];
  form: FormGroup;
  loaded = false;

  boundSaveWorkflows = this.saveWorkflows.bind(this);

  readonly onboardingProvider = inject(OnboardingProvider);
  private readonly workflowsProvider = inject(Workflows);
  private readonly formBuilder = inject(FormBuilder);
  private readonly util = inject(Util);
  private readonly storage = inject(TidyStorage);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly navCtrl = inject(CustomNavController);
  public readonly windowService = inject(WindowService);
  private readonly conciergeApi = inject(ConciergeApiService);

  constructor() {
    this.form = this.formBuilder.group({
      workflowRequest: [''],
    });
  }

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Automate Workflows');
    try {
      this.workflowTemplates = await this.workflowsProvider.getWorkflowTemplates();
      this.addedWorkflows = await this.workflowsProvider.getWorkflows();
      this.loaded = true;
      if (this.addedWorkflows.length > 0) {
        this.markWorkflowsAsSelected();
        this.addIDsForAlreadyAddedWorkflows();
      }
    } catch(err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  markWorkflowsAsSelected() {
    this.workflowTemplates.forEach((template) => {
      if (template.name === 'Friendly Reminder Email' || template.name === 'Job Alert' || template.name === 'Job Scheduled Email' || template.name === 'Same Day Job Scheduled Email') {
        template.isSelected = true;
      }
    });
  }

  addIDsForAlreadyAddedWorkflows() {
    this.workflowTemplates.forEach((template) => {
      if (template.name === 'Friendly Reminder Email' || template.name === 'Job Alert' || template.name === 'Job Scheduled Email' || template.name === 'Same Day Job Scheduled Email') {
        template['alreadyAddedID'] = this.addedWorkflows.find((workflow) => workflow.name === template.name).id;
      }
    });
  }

  selectTemplate(template) {
    if (template.isSelected && (template.name === 'Friendly Reminder Email' || template.name === 'Job Alert' || template.name === 'Job Scheduled Email' || template.name === 'Same Day Job Scheduled Email')) {
      this.unselectedAlreadyAddedTemplates.push(template);
    }
    if (!template.isSelected && (template.name === 'Friendly Reminder Email' || template.name === 'Job Alert' || template.name === 'Job Scheduled Email' || template.name === 'Same Day Job Scheduled Email')) {
      const index = this.unselectedAlreadyAddedTemplates.findIndex(t => t.name === template.name);
      if (index > -1) {
        this.unselectedAlreadyAddedTemplates.splice(index, 1);
      }
    }
    template.isSelected = !template.isSelected;
  }

  async saveWorkflows() {
    const selectedTemplates = this.workflowTemplates.filter((template) => template.isSelected);
    const workflowRequest = this.form.value.workflowRequest;
    const item = {
      data: {
        subject: 'Request Workflow(s)',
        body: workflowRequest,
        emails: [],
        attachments: [],
      },
      type: 'support.concierge_task',
    };
    try {
      if (workflowRequest.length > 0) {
        await lastValueFrom(this.conciergeApi.createConciergeItem(item));
      }
      await Promise.all([
        ...selectedTemplates.filter((template) => !template.alreadyAddedID).map((template) =>
          this.addTemplate(template.id)
        ),
        ...this.unselectedAlreadyAddedTemplates.map((template) =>
          this.removeWorkflow(template.alreadyAddedID)
        ),
        this.onboardingProvider.markGoalAsCompleted('automateWorkflows'),
      ]);
      this.onboardingProvider.setShowOnboardingInHeader(true);
      this.util.showSuccess('Workflows saved succesfully');
      this.rightSidePanelService.navigateTo('onboarding', {}, OnboardingComponent);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async removeWorkflow(workflowId) {
    await this.workflowsProvider.deleteWorkflow(workflowId);
  }

  async addTemplate(templateId) {
    const payload = {
      workflow_template_id: templateId,
    };
    await this.workflowsProvider.addWorkflowTemplate(payload);
  }

  async goToPage(url, params, component) {
    if (this.windowService.isDesktopRes) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve(
        'dialog-right-side-open'
      );
      if (isRightSidePanelAlreadyOpen) {
        return this.rightSidePanelService.navigateTo(
          url,
          params,
          component,
          false
        );
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

}
